// React imports
import { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Material UI imports
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import { BarChart } from '@mui/x-charts/BarChart';
import { grey, green, amber, red, blue, purple, cyan } from '@mui/material/colors';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseyeArrow, faCircleInfo, faTrophy, faChartLineUp, faBullseye, faListCheck, faCircleCheck, faCircleXmark, faArrowUpRightFromSquare, faPlane, faShip, faTruck } from '@fortawesome/pro-light-svg-icons';

// Local imports
import Header from '../../Header.js';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OpportunitiesFilter from '../components/OpportunitiesFilter.js';
import OpportunitiesFilterOwner from '../components/OpportunitiesFilter.owner.js';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons';
import { PIPELINE_TRADELINES } from '../accounts/AccountFunnelPipeline.js';
import PIPELINE_COLORS from '../accounts/AccountFunnelPipeline.colors.js';
import { getTradelinesStatus, getTradelineTimespan, goalToText } from './utils.js';
import { opportunitySellAndCost } from '../../utils/opportunityFinancials.js';
import currencyToText from '../../CurrencyToText.js';
import readableId from '../../ReadableId.js';
import { statusToText } from '../../OpportunityRow.utils.js';

dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

function transportModeLabel(o) {
    const tm = o.transportationMethod;
    if (tm === 'air') return 'Air';
    if (tm === 'land') return 'Land';
    const products = o.products || [];
    const hasLCL = products.some(p => (p.type || '').toLowerCase().includes('volume') || (p.type || '').toLowerCase().includes('weight') || (p.type || '').toLowerCase().includes('lcl'));
    if (hasLCL) return 'LCL';
    return 'FCL';
}

function calcRevenueAndCosts({ opportunities, exchangeRates, organization }) {
    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    let totalRevenue = 0;
    let totalCosts = 0;
    // The quote's own Sell and Cost (opportunitySellAndCost), so a quote counts the same here as on
    // its page and in Quotation Analytics.
    for (const o of opportunities) {
        const fin = opportunitySellAndCost({ opportunity: o, exchangeRates, organization });
        if (!fin) continue;
        if (isFinite(fin.sell)) totalRevenue += fin.sell;
        if (isFinite(fin.cost)) totalCosts += fin.cost;
    }
    const gp = totalRevenue - totalCosts;
    const margin = totalRevenue > 0 ? (gp / totalRevenue) * 100 : 0;
    return { revenue: totalRevenue, costs: totalCosts, gp, margin, format: fraction };
}

function TradelinesAnalytics() {
    const { org, opportunities, me, accounts, exchangeRates, tradelines } = useOutletContext();
    const navigate = useNavigate();
    const [drillDown, setDrillDown] = useState(null);

    const [ filter, setFilter ] = useState((() => {
        const filterStorage = sessionStorage.getItem('TradelinesAnalytics.filter');
        const base = { days: 'month', teamMember: me.email, accounts: [], taskStatus: 'all', taskType: 'all', tags: [], freetext: '' };
        try {
            return filterStorage ? JSON.parse(filterStorage) : base;
        } catch (e) {
            return base;
        }
    }));

    const [ targetStatusFilter, setTargetStatusFilter ] = useState('all');
    const [ originFilter, setOriginFilter ] = useState([]);
    const [ destinationFilter, setDestinationFilter ] = useState([]);
    const [ phaseFilter, setPhaseFilter ] = useState('all');

    function setFilterWithStorage(newFilter) {
        if (typeof newFilter === 'function') {
            setFilter(prev => {
                const resolved = newFilter(prev);
                sessionStorage.setItem('TradelinesAnalytics.filter', JSON.stringify(resolved));
                return resolved;
            });
        } else {
            sessionStorage.setItem('TradelinesAnalytics.filter', JSON.stringify(newFilter));
            setFilter(newFilter);
        }
    }

    const filteredOpportunities = useMemo(() => (opportunities || []).filter(r => r.status !== 'deleted'), [opportunities]);

    // Compute selected date range from filter
    const filterDateRange = useMemo(() => {
        if (!filter.days || filter.days === 'all') return null;
        const from = (() => {
            if (filter.days === 'week') return dayjs().startOf('isoWeek');
            if (filter.days === 'month') return dayjs().startOf('month');
            if (filter.days === 'quarter') return dayjs().startOf('quarter');
            if (filter.days === 'past-week') return dayjs().subtract(1, 'week').startOf('isoWeek');
            if (filter.days === 'past-month') return dayjs().subtract(1, 'month').startOf('month');
            if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').startOf('quarter');
            if (filter.days === 'custom' && filter.dateFrom) return dayjs(filter.dateFrom).startOf('day');
            return null;
        })();
        const to = (() => {
            if (filter.days === 'past-week') return dayjs().subtract(1, 'week').endOf('isoWeek');
            if (filter.days === 'past-month') return dayjs().subtract(1, 'month').endOf('month');
            if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').endOf('quarter');
            if (filter.days === 'custom' && filter.dateTo) return dayjs(filter.dateTo).endOf('day');
            return dayjs();
        })();
        if (!from) return null;
        return { start: from.valueOf(), end: to.valueOf() };
    }, [filter.days, filter.dateFrom, filter.dateTo]);

    // Calculate status for each tradeline, filtered by timespan overlap and account/freetext
    const tradelineStatuses = useMemo(() => {
        if (!tradelines || !opportunities || !exchangeRates || !org) return [];
        const filterAccountIds = (filter.accounts || []).flatMap(a => a.ids || []);
        const filterFreetext = (filter.freetext || []).map(f => f.toLowerCase());
        return (tradelines || []).map(tradeline => {
            // Filter by timespan overlap with selected date range
            if (filterDateRange) {
                const ts = getTradelineTimespan({ tradeline });
                if (ts.end < filterDateRange.start || ts.start > filterDateRange.end) return null;
            }
            // Filter by owner/member
            if (filter.members && filter.members.length > 0) {
                if (!filter.members.includes(tradeline.owner)) return null;
            }
            // Filter by transport mode
            if (filter.transportationMethods && filter.transportationMethods.length > 0) {
                if (!filter.transportationMethods.includes(tradeline.transportationMethod)) return null;
            }
            // Filter by account name
            if (filterAccountIds.length > 0 && tradeline.accountId && !filterAccountIds.includes(tradeline.accountId)) return null;
            // Filter by freetext (match against tradeline title, owner, or account name)
            if (filterFreetext.length > 0) {
                const account = (accounts || []).find(a => a && a.id === tradeline.accountId);
                const searchable = [tradeline.title, tradeline.owner, account?.name].filter(Boolean).join(' ').toLowerCase();
                if (!filterFreetext.every(ft => searchable.includes(ft))) return null;
            }
            try {
                const status = getTradelinesStatus({ opportunities: filteredOpportunities, exchangeRates, tradeline, organization: org });
                return { tradeline, ...status };
            } catch (e) {
                return { tradeline, results: {}, aggregated: { status: 'error', progress: 0 }, wonOpportunities: [], accumulatedValues: {} };
            }
        }).filter(Boolean);
    }, [tradelines, filteredOpportunities, exchangeRates, org, filterDateRange, filter.accounts, filter.freetext, filter.members, filter.transportationMethods, accounts]);

    const displayedStatuses = tradelineStatuses.filter(t => {
        if (targetStatusFilter !== 'all' && t.aggregated?.status !== targetStatusFilter) return false;
        if (phaseFilter !== 'all') {
            const phase = (t.tradeline.tags || []).find(tag => tag.label?.startsWith('Pipeline: '))?.label?.replace('Pipeline: ', '');
            if ((phase || 'New') !== phaseFilter) return false;
        }
        if (originFilter.length > 0) {
            const origins = (t.tradeline.routes || []).map(r => r.source?.text || 'Everywhere');
            if (!originFilter.some(f => origins.some(o => o.toLowerCase().includes(f.toLowerCase())))) return false;
        }
        if (destinationFilter.length > 0) {
            const destinations = (t.tradeline.routes || []).map(r => r.destination?.text || 'Everywhere');
            if (!destinationFilter.some(f => destinations.some(d => d.toLowerCase().includes(f.toLowerCase())))) return false;
        }
        return true;
    });

    // Origin/destination options for filter autocomplete
    const originOptions = _.sortBy(_.uniq(tradelineStatuses.flatMap(t => (t.tradeline.routes || []).map(r => r.source?.text || 'Everywhere'))).filter(Boolean));
    const destinationOptions = _.sortBy(_.uniq(tradelineStatuses.flatMap(t => (t.tradeline.routes || []).map(r => r.destination?.text || 'Everywhere'))).filter(Boolean));

    if (!opportunities || !exchangeRates || !accounts || !tradelines) {
        return (
            <Box sx={{ padding: '36px 0px', background: '#fafbfc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={18} />
            </Box>
        );
    }

    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const cur = org.defaults?.currency || 'USD';

    // Aggregate KPIs from displayed (filtered) statuses
    const totalTargets = displayedStatuses.length;
    const achievedTargets = displayedStatuses.filter(t => t.aggregated?.status === 'completed').length;
    const failedTargets = displayedStatuses.filter(t => t.aggregated?.status === 'failed').length;
    const inProgressTargets = displayedStatuses.filter(t => t.aggregated?.status === 'inprogress').length;
    const avgProgress = totalTargets > 0 ? _.meanBy(displayedStatuses, t => Math.min(t.aggregated?.progress || 0, 1)) * 100 : 0;

    // All won opportunities across displayed tradelines
    const allWon = _.uniqBy(displayedStatuses.flatMap(t => t.wonOpportunities || []), 'id');
    const rc = calcRevenueAndCosts({ opportunities: allWon, exchangeRates, organization: org });

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header icon={faBullseyeArrow} title='Sales Performance' />

            <Box style={{ padding: '12px', paddingTop: '0px' }}>
            {/* Sales Targets Detail */}
            <Paper sx={{ padding: '24px', margin: '0px 12px', flex: 1 }}>
                <Box sx={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${grey[300]}` }}>
                    <Typography variant='body2' component={Box} sx={{ fontWeight: 800 }}>
                        Sales Targets Detail
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        Progress against each individual sales target{originFilter.length > 0 || destinationFilter.length > 0 ? ' (filtered)' : ''}. Click any row to see contributing opportunities.
                    </Typography>
                </Box>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box sx={{ maxWidth: '200px' }}>
                        <OpportunitiesFilterOwner
                            org={org}
                            me={me}
                            filter={filter}
                            setFilter={setFilterWithStorage}
                            noMarginRight
                        />
                    </Box>
                    <Select
                        size='small'
                        value={filter.days}
                        onChange={(e) => setFilterWithStorage({ ...filter, days: e.target.value })}
                        renderValue={(val) => {
                            const labels = { week: 'This Week', month: 'This Month', quarter: 'This Quarter', 'past-week': 'Last Week', 'past-month': 'Last Month', 'past-quarter': 'Last Quarter', custom: 'Custom Range', all: 'All Time' };
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: '12px', opacity: 0.5 }} />
                                    <span>{labels[val] || val}</span>
                                </Box>
                            );
                        }}
                        sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', '& .MuiSelect-select': { py: '6px' }, minWidth: 0 }}
                    >
                        <MenuItem value='week'>This Week</MenuItem>
                        <MenuItem value='month'>This Month</MenuItem>
                        <MenuItem value='quarter'>This Quarter</MenuItem>
                        <MenuItem value='past-week'>Last Week</MenuItem>
                        <MenuItem value='past-month'>Last Month</MenuItem>
                        <MenuItem value='past-quarter'>Last Quarter</MenuItem>
                        <MenuItem value='custom'>Custom Range</MenuItem>
                        <MenuItem value='all'>All Time</MenuItem>
                    </Select>
                    {filter.days === 'custom' && (
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <MobileDatePicker
                                label="From"
                                inputFormat="DD/MM/YYYY"
                                value={filter.dateFrom || null}
                                onChange={(v) => setFilterWithStorage({ ...filter, dateFrom: v })}
                                renderInput={(params) => <TextField size='small' sx={{ width: 140, '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }} {...params} />}
                            />
                            <MobileDatePicker
                                label="To"
                                inputFormat="DD/MM/YYYY"
                                value={filter.dateTo || null}
                                onChange={(v) => setFilterWithStorage({ ...filter, dateTo: v })}
                                renderInput={(params) => <TextField size='small' sx={{ width: 140, '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }} {...params} />}
                            />
                        </Box>
                    )}
                    <Select
                        size='small'
                        value={phaseFilter}
                        onChange={e => setPhaseFilter(e.target.value)}
                        sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', minWidth: 130, '& .MuiSelect-select': { py: '6px' } }}
                    >
                        <MenuItem value='all'>All Phases</MenuItem>
                        {PIPELINE_TRADELINES.map(phase => (
                            <MenuItem key={phase} value={phase}>{phase}</MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Autocomplete
                        multiple freeSolo size='small'
                        options={originOptions}
                        value={originFilter}
                        onChange={(e, v) => setOriginFilter(v)}
                        filterOptions={(options, { inputValue }) => inputValue ? options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())) : options}
                        renderInput={(params) => <TextField {...params} label='Filter by Origin' placeholder='Type to search...' />}
                        sx={{ flex: 1, minWidth: '180px', '& .MuiInputBase-root': { fontSize: '12px' } }}
                    />
                    <Autocomplete
                        multiple freeSolo size='small'
                        options={destinationOptions}
                        value={destinationFilter}
                        onChange={(e, v) => setDestinationFilter(v)}
                        filterOptions={(options, { inputValue }) => inputValue ? options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())) : options}
                        renderInput={(params) => <TextField {...params} label='Filter by Destination' placeholder='Type to search...' />}
                        sx={{ flex: 1, minWidth: '180px', '& .MuiInputBase-root': { fontSize: '12px' } }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: '6px', mb: 2 }}>
                    {[
                        { key: 'all', label: 'All', count: tradelineStatuses.length },
                        { key: 'completed', label: 'Achieved', count: tradelineStatuses.filter(t => t.aggregated?.status === 'completed').length, color: green[600] },
                        { key: 'inprogress', label: 'In Progress', count: tradelineStatuses.filter(t => t.aggregated?.status === 'inprogress').length, color: amber[700] },
                        { key: 'failed', label: 'Missed', count: tradelineStatuses.filter(t => t.aggregated?.status === 'failed').length, color: red[500] },
                    ].map(pill => (
                        <Chip
                            key={pill.key}
                            label={`${pill.label} (${pill.count})`}
                            size='small'
                            onClick={() => setTargetStatusFilter(targetStatusFilter === pill.key ? 'all' : pill.key)}
                            sx={{
                                fontSize: '11px',
                                fontWeight: targetStatusFilter === pill.key ? 700 : 400,
                                backgroundColor: targetStatusFilter === pill.key ? (pill.color || grey[700]) : 'transparent',
                                color: targetStatusFilter === pill.key ? '#fff' : 'text.secondary',
                                border: `1px solid ${targetStatusFilter === pill.key ? 'transparent' : grey[300]}`,
                                '&:hover': { backgroundColor: targetStatusFilter === pill.key ? (pill.color || grey[700]) : grey[100] },
                            }}
                        />
                    ))}
                </Box>

                {/* KPIs */}
                <Box sx={{ display: 'flex', gap: '8px', mb: 3 }}>
                    <KPICard title='Total Targets' value={totalTargets} icon={faBullseye} color={blue}
                        info='Total number of active sales targets' />
                    <KPICard title='Achieved' value={`${achievedTargets} (${totalTargets > 0 ? fraction.format(achievedTargets / totalTargets * 100) : 0}%)`}
                        icon={faCircleCheck} color={green}
                        caption={`${inProgressTargets} in progress · ${failedTargets} missed`}
                        info='Targets where actual progress reached or exceeded the goal' />
                    <KPICard title='Avg Progress' value={`${fraction.format(avgProgress)}%`}
                        icon={faChartLineUp} color={purple}
                        caption='Average achievement across all targets'
                        info='Mean progress percentage across all active targets (capped at 100% per target)' />
                    <KPICard title='Revenue (Won)' value={currencyToText(cur, true, fraction.format(rc.revenue).toLocaleString())}
                        icon={faTrophy} color={amber}
                        caption={`From ${allWon.length} won deal${allWon.length === 1 ? '' : 's'}`}
                        info='Total sell value from won opportunities that contributed to sales targets'
                        onClick={() => setDrillDown({ title: 'Won Deals (Targets)', opportunities: allWon })} />
                    <KPICard title='Gross Profit' value={currencyToText(cur, true, fraction.format(rc.gp).toLocaleString())}
                        icon={faChartLineUp} color={rc.gp >= 0 ? green : red}
                        caption={rc.revenue > 0 ? `${fraction.format(rc.margin)}% margin` : ''}
                        info='Revenue minus costs from won opportunities contributing to targets'
                        onClick={() => setDrillDown({ title: 'Won Deals (Targets)', opportunities: allWon })} />
                </Box>

                {/* Table */}
                <SalesTargetsDetailTable tradelineStatuses={displayedStatuses} accounts={accounts} org={org} fraction={fraction} onDrillDown={setDrillDown} navigate={navigate} />
            </Paper>

            {/* Owner Performance */}
            <Paper sx={{ padding: '24px', margin: '12px', flex: 1 }}>
                <Box sx={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${grey[300]}` }}>
                    <Typography variant='body2' component={Box} sx={{ fontWeight: 800 }}>
                        Performance by Owner
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        How each team member is performing against their assigned targets.
                    </Typography>
                </Box>
                <OwnerPerformanceTable tradelineStatuses={displayedStatuses} accounts={accounts} exchangeRates={exchangeRates} organization={org} onDrillDown={setDrillDown} />
            </Paper>

            </Box>

            {/* Drill-down dialog */}
            {drillDown && (
                <Dialog open={true} onClose={() => setDrillDown(null)} maxWidth='md' fullWidth>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                        <Typography variant='h6' sx={{ fontSize: '16px' }}>{drillDown.title} ({drillDown.opportunities.length})</Typography>
                        <Button size='small' onClick={() => setDrillDown(null)}>Close</Button>
                    </DialogTitle>
                    <DialogContent>
                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Opportunity ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Mode</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Account</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Owner</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Created</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align='right'>Revenue</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align='right'>Gross Profit</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {drillDown.opportunities.map(o => {
                                    const rc = calcRevenueAndCosts({ opportunities: [o], exchangeRates, organization: org });
                                    const account = (accounts || []).find(a => a && a.id === o.account?.id);
                                    return (
                                        <TableRow
                                            key={o.id}
                                            hover
                                            sx={{ cursor: 'pointer', '& td': { fontSize: '12px' } }}
                                            onClick={() => window.open(`/organization/${org.alias}/opportunities/${o.id}/`, '_blank')}
                                        >
                                            <TableCell sx={{ fontWeight: 600 }}>{readableId(o)}</TableCell>
                                            <TableCell>{transportModeLabel(o)}</TableCell>
                                            <TableCell>{account?.name || '—'}</TableCell>
                                            <TableCell>{(o.owner || '').split('@')[0]}</TableCell>
                                            <TableCell><Chip size='small' label={statusToText({ opportunity: o }) || 'Draft'} sx={{ fontSize: '10px', height: '20px' }} /></TableCell>
                                            <TableCell>{dayjs(o.created).format('MMM D, YYYY')}</TableCell>
                                            <TableCell align='right'>{rc.revenue > 0 ? currencyToText(cur, true, rc.format.format(rc.revenue)) : '—'}</TableCell>
                                            <TableCell align='right' sx={{ color: rc.gp >= 0 ? green[700] : red[500], fontWeight: 600 }}>{rc.revenue > 0 ? currencyToText(cur, true, rc.format.format(rc.gp)) : '—'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
}

function KPICard({ title, value, caption, icon, color, info, onClick }) {
    const [hover, setHover] = useState(false);
    return (
        <Box
            sx={{
                display: 'flex', flexDirection: 'column', padding: '16px 20px',
                border: `1px solid ${grey[300]}`, borderRadius: '8px', background: 'white',
                flex: 1, minWidth: 0,
                ...(hover && { border: `1px solid ${blue[500]}` }),
                ...(onClick && { cursor: 'pointer' })
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={onClick}
        >
            <Box sx={{ display: 'flex', marginBottom: '6px', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography variant='body2' component={Box} sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {title}
                        {info && (
                            <Tooltip title={info} placement='top' arrow>
                                <Box component='span' sx={{ cursor: 'help', opacity: 0.4, fontSize: '11px', '&:hover': { opacity: 0.8 } }}>
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </Box>
                            </Tooltip>
                        )}
                    </Typography>
                    <Typography variant='h6' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                        {value}
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: color[100], color: color[500],
                    width: '50px', height: '50px', borderRadius: '12px',
                    marginLeft: '12px', fontSize: '1.2em'
                }}>
                    <FontAwesomeIcon icon={icon} />
                </Box>
            </Box>
            <Typography variant='caption' component={Box} sx={{ color: grey[500], minHeight: '32px', fontSize: '11px', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                {caption}
            </Typography>
        </Box>
    );
}

function OwnerPerformanceTable({ tradelineStatuses, accounts, exchangeRates, organization, onDrillDown }) {
    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const cur = organization.defaults?.currency || 'USD';

    // Group by owner
    const owners = _.uniq(tradelineStatuses.map(t => t.tradeline.owner || 'unknown'));

    const rows = owners.map(owner => {
        const ownerTradelines = tradelineStatuses.filter(t => (t.tradeline.owner || 'unknown') === owner);
        const totalTargets = ownerTradelines.length;
        const achieved = ownerTradelines.filter(t => t.aggregated?.status === 'completed').length;
        const avgProgress = totalTargets > 0 ? _.meanBy(ownerTradelines, t => Math.min(t.aggregated?.progress || 0, 1)) * 100 : 0;
        const allWon = _.uniqBy(ownerTradelines.flatMap(t => t.wonOpportunities || []), 'id');
        const rc = calcRevenueAndCosts({ opportunities: allWon, exchangeRates, organization });

        return {
            owner: owner === 'unknown' ? '—' : owner.split('@')[0],
            targets: totalTargets,
            achieved,
            avgProgress,
            won: allWon.length,
            revenue: rc.revenue,
            gp: rc.gp,
            margin: rc.margin,
            wonOpps: allWon,
        };
    }).filter(r => r.targets > 0);

    return (
        <TableContainer>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        {[
                            { text: 'Created By', info: 'Team member who created the sales target' },
                            { text: 'Targets', info: 'Number of sales targets assigned' },
                            { text: 'Achieved', info: 'Targets where progress reached 100%' },
                            { text: 'Avg Progress', info: 'Average achievement across all assigned targets' },
                            { text: 'Won Deals', info: 'Number of won opportunities contributing to targets' },
                            { text: 'Revenue', info: 'Total sell value from won deals' },
                            { text: 'Profit', info: 'Revenue minus costs from won deals' },
                            { text: 'Margin', info: 'Profit as a percentage of revenue' },
                        ].map(h => (
                            <TableCell key={h.text} sx={{ fontWeight: 700, fontSize: '12px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {h.text}
                                    <Tooltip title={h.info} placement='top' arrow>
                                        <Box component='span' sx={{ cursor: 'help', opacity: 0.3, fontSize: '11px', '&:hover': { opacity: 0.7 } }}>
                                            <FontAwesomeIcon icon={faCircleInfo} />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(r => (
                        <TableRow
                            key={r.owner}
                            hover
                            sx={{ cursor: 'pointer', '& td': { fontSize: '12px' } }}
                            onClick={() => onDrillDown && onDrillDown({ title: r.owner, opportunities: r.wonOpps })}
                        >
                            <TableCell sx={{ fontWeight: 500 }}>{r.owner}</TableCell>
                            <TableCell>{r.targets}</TableCell>
                            <TableCell>
                                <Typography sx={{ fontWeight: 600, color: r.achieved > 0 ? green[700] : 'text.secondary', fontSize: '12px' }}>
                                    {r.achieved} / {r.targets}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <LinearProgress
                                        variant='determinate'
                                        value={Math.min(r.avgProgress, 100)}
                                        sx={{
                                            width: 60, height: 6, borderRadius: 3,
                                            backgroundColor: grey[200],
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: r.avgProgress >= 100 ? green[500] : r.avgProgress >= 50 ? amber[500] : red[400],
                                                borderRadius: 3,
                                            }
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>{fraction.format(r.avgProgress)}%</Typography>
                                </Box>
                            </TableCell>
                            <TableCell>{r.won}</TableCell>
                            <TableCell>{currencyToText(cur, true, fraction.format(r.revenue).toLocaleString())}</TableCell>
                            <TableCell>
                                <Typography sx={{ fontWeight: 700, color: r.gp >= 0 ? green[700] : red[500], fontSize: '13px' }}>
                                    {currencyToText(cur, true, fraction.format(r.gp).toLocaleString())}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography sx={{ fontWeight: 600, fontSize: '13px', color: r.margin >= 15 ? green[700] : r.margin >= 10 ? amber[800] : red[500] }}>
                                    {fraction.format(r.margin)}%
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function getTradeDirection({ tradeline, orgAddress }) {
    if (!orgAddress?.countryCode || !tradeline.routes?.length) return 'All';
    const orgCountry = orgAddress.countryCode?.toUpperCase();
    let hasImport = false, hasExport = false;
    for (const route of tradeline.routes) {
        const srcCountry = route.source?.countryCode?.toUpperCase();
        const dstCountry = route.destination?.countryCode?.toUpperCase();
        if (!srcCountry && !dstCountry) continue;
        const fromHome = srcCountry === orgCountry;
        const toHome = dstCountry === orgCountry;
        if (toHome && !fromHome) hasImport = true;
        else if (fromHome && !toHome) hasExport = true;
        else if (!fromHome && !toHome) { hasImport = true; hasExport = true; } // cross-trade
        else if (fromHome && toHome) { hasImport = true; hasExport = true; } // domestic
    }
    if (hasImport && hasExport) return 'Both';
    if (hasImport) return 'Import';
    if (hasExport) return 'Export';
    return 'All';
}

function SalesTargetsDetailTable({ tradelineStatuses, accounts, org, fraction, onDrillDown, navigate }) {
    const [sortBy, setSortBy] = useState('target');
    const [sortDir, setSortDir] = useState('asc');

    const toggleSort = (col) => {
        if (sortBy === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(col);
            setSortDir('asc');
        }
    };

    const rows = tradelineStatuses.map(({ tradeline, results, aggregated, wonOpportunities }) => {
        const account = (accounts || []).find(a => a && a.id === tradeline.accountId);
        const progress = Math.min((aggregated?.progress || 0) * 100, 100);
        const statusOrder = aggregated?.status === 'completed' ? 0 : aggregated?.status === 'inprogress' ? 1 : 2;
        const direction = getTradeDirection({ tradeline, orgAddress: org.address });

        // Extract cargo/revenue/profit from goals
        const cargoGoal = (tradeline.goals || []).find(g => g.type === 'units' || g.type === 'chargeable-weight' || g.type === 'gross-weight' || g.type === 'shipments');
        const revenueGoal = (tradeline.goals || []).find(g => g.type === 'revenue');
        const profitGoal = (tradeline.goals || []).find(g => g.type === 'profit');
        const cargoResult = cargoGoal ? results[cargoGoal.id] : null;
        const revenueResult = revenueGoal ? results[revenueGoal.id] : null;
        const profitResult = profitGoal ? results[profitGoal.id] : null;
        const cargoValue = cargoResult?.value || 0;
        const revenueValue = revenueResult?.value || 0;
        const profitValue = profitResult?.value || 0;

        const phase = (tradeline.tags || []).find(t => t.label?.startsWith('Pipeline: '))?.label?.replace('Pipeline: ', '') || 'New';
        const origins = _.uniq((tradeline.routes || []).map(r => r.source?.text || 'Everywhere').filter(Boolean));
        const destinations = _.uniq((tradeline.routes || []).map(r => r.destination?.text || 'Everywhere').filter(Boolean));

        // Clean title — remove account name, transport mode, and route info
        let cleanTitle = tradeline.title || '(unnamed)';
        if (account?.name) cleanTitle = cleanTitle.replace(account.name, '').replace(account.name?.toUpperCase(), '');
        cleanTitle = cleanTitle.replace(/\b(sea|air|land)\b/gi, '').replace(/→|->|—/g, '').trim();
        origins.forEach(o => { cleanTitle = cleanTitle.replace(o, ''); });
        destinations.forEach(d => { cleanTitle = cleanTitle.replace(d, ''); });
        cleanTitle = cleanTitle.replace(/^[\s\-·|,]+|[\s\-·|,]+$/g, '').trim() || tradeline.title || '(unnamed)';

        return { tradeline, results, aggregated, wonOpportunities, account, progress, statusOrder, direction, phase, origins, destinations, cleanTitle,
            cargoGoal, revenueGoal, profitGoal, cargoResult, revenueResult, profitResult, cargoValue, revenueValue, profitValue };
    });

    const sorted = _.orderBy(rows, [r => {
        if (sortBy === 'target') return (r.tradeline.title || '').toLowerCase();
        if (sortBy === 'owner') return (r.tradeline.opportunitiesOwner || '').toLowerCase();
        if (sortBy === 'account') return (r.account?.name || '').toLowerCase();
        if (sortBy === 'period') return r.tradeline.recurringPeriod || '';
        if (sortBy === 'mode') return r.tradeline.transportationMethod || '';
        if (sortBy === 'origin') return (r.origins[0] || '').toLowerCase();
        if (sortBy === 'destination') return (r.destinations[0] || '').toLowerCase();
        if (sortBy === 'direction') return r.direction;
        if (sortBy === 'phase') return r.phase;
        if (sortBy === 'cargo') return r.cargoValue;
        if (sortBy === 'revenue') return r.revenueValue;
        if (sortBy === 'profit') return r.profitValue;
        if (sortBy === 'progress') return r.progress;
        if (sortBy === 'status') return r.statusOrder;
        return 0;
    }], [sortDir]);

    const headers = [
        { key: 'account', text: 'Account' },
        { key: 'origin', text: 'Origin' },
        { key: 'destination', text: 'Destination' },
        { key: 'owner', text: 'Owner' },
        { key: 'mode', text: 'Mode' },
        { key: 'direction', text: 'Direction' },
        { key: 'phase', text: 'Phase' },
        { key: 'cargo', text: 'Cargo' },
        { key: 'revenue', text: 'Revenue' },
        { key: 'profit', text: 'Profit' },
        { key: 'progress', text: 'Progress', minWidth: 200 },
        { key: 'status', text: 'Status' },
        { key: 'open', text: '', noSort: true },
    ];

    return (
        <TableContainer>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        {headers.map(h => (
                            <TableCell key={h.key} sx={{ fontWeight: 700, fontSize: '12px', ...(h.minWidth ? { minWidth: h.minWidth } : {}) }}>
                                {h.noSort ? h.text : (
                                    <TableSortLabel
                                        active={sortBy === h.key}
                                        direction={sortBy === h.key ? sortDir : 'asc'}
                                        onClick={() => toggleSort(h.key)}
                                        disabled={false}
                                    >
                                        {h.text}
                                    </TableSortLabel>
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map(({ tradeline, results, aggregated, wonOpportunities, account, progress, direction, phase, origins, destinations, cleanTitle, cargoGoal, revenueGoal, profitGoal, cargoResult, revenueResult, profitResult }) => {
                        const statusColor = aggregated?.status === 'completed' ? green[600] : aggregated?.status === 'failed' ? red[500] : amber[700];
                        const statusLabel = aggregated?.status === 'completed' ? 'Achieved' : aggregated?.status === 'failed' ? 'Missed' : 'In Progress';

                        return (
                            <TableRow
                                key={tradeline.id}
                                hover
                                sx={{ cursor: 'pointer', '& td': { fontSize: '12px' } }}
                                onClick={() => onDrillDown && onDrillDown({ title: tradeline.title || '(unnamed)', opportunities: wonOpportunities || [] })}
                            >
                                <TableCell sx={{ fontWeight: 600 }}>
                                    {account?.id ? (
                                        <Typography
                                            component='span'
                                            sx={{ fontSize: '12px', fontWeight: 600, color: '#3b82f6', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                            onClick={(e) => { e.stopPropagation(); window.open(`/organization/${org.alias}/accounts/${account.id}/details`, '_blank'); }}
                                        >
                                            {account.name}
                                        </Typography>
                                    ) : 'All accounts'}
                                </TableCell>
                                <TableCell>
                                    <Typography sx={{ fontSize: '11px' }}>{origins.join(', ') || '—'}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography sx={{ fontSize: '11px' }}>{destinations.join(', ') || '—'}</Typography>
                                </TableCell>
                                <TableCell>{(tradeline.owner || '').split('@')[0] || '—'}</TableCell>
                                <TableCell>
                                    <Tooltip title={_.capitalize(tradeline.transportationMethod || '')} placement='top'>
                                        <Box component='span' sx={{ fontSize: '14px', color: grey[600] }}>
                                            <FontAwesomeIcon icon={tradeline.transportationMethod === 'air' ? faPlane : tradeline.transportationMethod === 'land' ? faTruck : faShip} />
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={direction}
                                        size='small'
                                        sx={{
                                            fontSize: '10px', height: '20px',
                                            backgroundColor: direction === 'Import' ? blue[50] : direction === 'Export' ? green[50] : direction === 'Both' ? purple[50] : grey[100],
                                            color: direction === 'Import' ? blue[700] : direction === 'Export' ? green[700] : direction === 'Both' ? purple[700] : grey[600],
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {(() => {
                                        const phaseIndex = PIPELINE_TRADELINES.indexOf(phase);
                                        const phaseColor = phaseIndex >= 0 ? PIPELINE_COLORS[phaseIndex] : '#ABB8C3';
                                        return (
                                            <Chip
                                                label={phase}
                                                size='small'
                                                sx={{ fontSize: '10px', height: '20px', backgroundColor: `${phaseColor}20`, color: phaseColor, fontWeight: 700, border: `1px solid ${phaseColor}40` }}
                                            />
                                        );
                                    })()}
                                </TableCell>
                                <TableCell>
                                    {cargoGoal && cargoResult ? (
                                        <Typography sx={{ fontSize: '11px' }}>
                                            <span style={{ color: '#6b7280' }}>{cargoGoal.type === 'units' ? (tradeline.transportationMethod === 'air' ? 'CW' : 'TEU') : cargoGoal.type === 'chargeable-weight' ? 'CW' : cargoGoal.type === 'gross-weight' ? 'Weight' : 'Shipments'}: </span>
                                            <b>{fraction.format(cargoResult.value)}</b><span style={{ color: '#9ca3af' }}> / {fraction.format(cargoResult.goal)}</span>
                                        </Typography>
                                    ) : <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>—</Typography>}
                                </TableCell>
                                <TableCell>
                                    {revenueGoal && revenueResult ? (
                                        <Typography sx={{ fontSize: '11px' }}>
                                            <b>{currencyToText(revenueGoal.currency, true, fraction.format(revenueResult.value))}</b><span style={{ color: '#9ca3af' }}> / {currencyToText(revenueGoal.currency, true, fraction.format(revenueResult.goal))}</span>
                                        </Typography>
                                    ) : <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>—</Typography>}
                                </TableCell>
                                <TableCell>
                                    {profitGoal && profitResult ? (
                                        <Typography sx={{ fontSize: '11px' }}>
                                            <b style={{ color: profitResult.value >= 0 ? green[700] : red[500] }}>{currencyToText(profitGoal.currency, true, fraction.format(profitResult.value))}</b><span style={{ color: '#9ca3af' }}> / {currencyToText(profitGoal.currency, true, fraction.format(profitResult.goal))}</span>
                                        </Typography>
                                    ) : <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>—</Typography>}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <LinearProgress
                                            variant='determinate'
                                            value={progress}
                                            sx={{
                                                flex: 1, height: 8, borderRadius: 4,
                                                backgroundColor: grey[200],
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: statusColor,
                                                    borderRadius: 4,
                                                }
                                            }}
                                        />
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                                            {fraction.format(progress)}%
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={statusLabel}
                                        size='small'
                                        sx={{ fontSize: '10px', height: '20px', backgroundColor: statusColor, color: '#fff' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Tooltip title='Open sales target' placement='top'>
                                        <IconButton
                                            size='small'
                                            sx={{ color: grey[400], '&:hover': { color: blue[500] } }}
                                            onClick={(e) => { e.stopPropagation(); window.open(`/organization/${org.alias}/tradelines/${tradeline.id}`, '_blank'); }}
                                        >
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '12px' }} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default TradelinesAnalytics;
